<div class="bg-night text-white max-h-[90vh] overflow-hidden overflow-x-hidden flex flex-col relative">
  @if (duplicateConfirmOrder(); as confirmOrder) {
    <div class="absolute inset-0 bg-night/95 z-50 flex flex-col">
      <div class="p-4 lg:p-6 border-b border-white/10 flex items-center justify-between">
        <h2 class="text-lg lg:text-xl font-bold flex items-center gap-3">
          <span class="material-symbols-outlined text-neon">content_copy</span>
          {{ 'pos.tabs.duplicate_confirm_title' | translate }}
        </h2>
        <button
          type="button"
          (click)="cancelDuplicate()"
          [disabled]="duplicating()"
          class="w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 transition-colors flex items-center justify-center disabled:opacity-50">
          <span class="material-symbols-outlined">close</span>
        </button>
      </div>

      <div class="flex-1 overflow-y-auto p-4 lg:p-6">
        <p class="text-sm text-white/70 mb-4">{{ 'pos.tabs.duplicate_confirm_message' | translate }}</p>

        <div class="bg-white/5 rounded-xl overflow-hidden mb-4">
          @for (item of confirmOrder.items; track item.id) {
            <div class="px-4 py-3 border-b border-white/5 last:border-b-0 flex items-center justify-between">
              <div class="flex-1 min-w-0">
                <span class="text-sm text-white">{{ item.name }}</span>
                <span class="text-sm text-white/50 ml-2">x{{ item.qty }}</span>
              </div>
              <span class="text-sm font-semibold text-white shrink-0"
                >{{ item.lineTotalCents / 100 | number: '1.2-2' }} €</span
              >
            </div>
          }
        </div>

        <div class="bg-neon/10 border border-neon/30 rounded-xl p-4">
          <div class="flex items-center justify-between">
            <span class="text-sm text-white/70">{{ 'pos.tabs.detail.total' | translate }}</span>
            <span class="text-xl font-bold text-neon">{{ confirmOrder.totalCents / 100 | number: '1.2-2' }} €</span>
          </div>
        </div>
      </div>

      <div class="p-4 lg:p-6 border-t border-white/10 space-y-3">
        <button
          type="button"
          data-testid="duplicate-confirm"
          (click)="confirmDuplicate()"
          [disabled]="duplicating()"
          class="w-full py-3 px-6 rounded-full bg-neon text-night font-bold hover:bg-neon/80 transition-all flex items-center justify-center gap-2 uppercase disabled:opacity-50">
          @if (duplicating()) {
            <div class="w-5 h-5 border-2 border-night/20 border-t-night rounded-full animate-spin"></div>
          } @else {
            <span class="material-symbols-outlined">check</span>
          }
          {{ 'pos.tabs.duplicate_confirm_button' | translate }}
        </button>

        <button
          type="button"
          data-testid="duplicate-cancel"
          (click)="cancelDuplicate()"
          [disabled]="duplicating()"
          class="w-full py-3 px-6 rounded-full border border-white/20 text-white font-medium hover:bg-white/10 transition-colors flex items-center justify-center gap-2 disabled:opacity-50">
          <span class="material-symbols-outlined">close</span>
          {{ 'admin.common.cancel' | translate }}
        </button>
      </div>
    </div>
  }

  <div class="p-4 lg:p-6 border-b border-white/10 flex items-center justify-between">
    <div class="flex items-center gap-3">
      <div
        class="w-12 h-12 rounded-full flex items-center justify-center"
        [ngClass]="{
          'bg-blue-500/20': tab.type === PosTabType.User,
          'bg-orange-500/20': tab.type === PosTabType.Visitor,
        }">
        <span
          class="material-symbols-outlined text-2xl"
          [ngClass]="{
            'text-blue-400': tab.type === PosTabType.User,
            'text-orange-400': tab.type === PosTabType.Visitor,
          }">
          {{ tab.type === PosTabType.User ? 'person' : 'person_outline' }}
        </span>
      </div>
      <div>
        <h2 class="text-lg lg:text-xl font-bold">{{ getDisplayName() }}</h2>
        <p class="text-xs text-white/50">{{ tab.createdAt | date: 'medium' }}</p>
      </div>
    </div>
    <button
      type="button"
      (click)="close()"
      class="w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 transition-colors flex items-center justify-center">
      <span class="material-symbols-outlined">close</span>
    </button>
  </div>

  <div class="flex-1 overflow-y-auto p-4 lg:p-6">
    @if (loading()) {
      <div class="flex items-center justify-center py-12">
        <div class="w-10 h-10 border-4 border-neon/20 border-t-neon rounded-full animate-spin"></div>
      </div>
    } @else if (detail(); as d) {
      <div class="space-y-4">
        <div class="bg-white/5 rounded-xl p-4 space-y-3">
          <h3 class="text-sm font-semibold text-white flex items-center gap-2">
            <span class="material-symbols-outlined text-base text-neon">euro</span>
            {{ 'pos.tabs.detail.money_section' | translate }}
          </h3>
          <div class="grid grid-cols-3 gap-3">
            <div>
              <p class="text-xs text-white/50 mb-1">{{ 'pos.tabs.detail.total' | translate }}</p>
              <p
                class="text-xl font-bold text-white"
                data-testid="tab-detail-total"
                >{{ d.orderTotalCents / 100 | number: '1.2-2' }} €</p
              >
            </div>
            <div>
              <p class="text-xs text-white/50 mb-1">{{ 'pos.tabs.detail.paid' | translate }}</p>
              <p
                class="text-xl font-bold text-green-400"
                data-testid="tab-detail-paid"
                >{{ d.orderPaidCents / 100 | number: '1.2-2' }} €</p
              >
              @if (d.pointsPaidCents > 0) {
                <p
                  class="text-xs text-amber-400 mt-1"
                  data-testid="tab-detail-points-paid">
                  {{ 'pos.tabs.detail.paid_with_points' | translate }} {{ d.pointsPaidCents / 100 | number: '1.2-2' }} €
                </p>
              }
            </div>
            <div>
              <p class="text-xs text-white/50 mb-1">{{ 'pos.tabs.detail.balance' | translate }}</p>
              <p
                class="text-xl font-bold"
                data-testid="tab-detail-balance"
                [class.text-neon]="d.orderBalanceCents > 0"
                [class.text-green-400]="d.orderBalanceCents <= 0">
                {{ d.orderBalanceCents / 100 | number: '1.2-2' }} €
              </p>
            </div>
          </div>
        </div>

        @if (d.orderTotalPoints > 0 || d.paidPoints > 0 || d.orderBalancePoints > 0) {
          <div class="bg-amber-500/10 border border-amber-500/20 rounded-xl p-4 space-y-3">
            <h3 class="text-sm font-semibold text-white flex items-center gap-2">
              <span class="material-symbols-outlined text-base text-amber-400">star</span>
              {{ 'pos.tabs.detail.points_section' | translate }}
            </h3>
            <div class="grid grid-cols-3 gap-3">
              <div>
                <p class="text-xs text-white/50 mb-1">{{ 'pos.tabs.detail.total_points' | translate }}</p>
                <p class="text-xl font-bold text-amber-400">{{ d.orderTotalPoints }} b.</p>
              </div>
              <div>
                <p class="text-xs text-white/50 mb-1">{{ 'pos.tabs.detail.paid_points' | translate }}</p>
                <p class="text-xl font-bold text-green-400">{{ d.paidPoints }} b.</p>
              </div>
              <div>
                <p class="text-xs text-white/50 mb-1">{{ 'pos.tabs.detail.balance_points' | translate }}</p>
                <p
                  class="text-xl font-bold"
                  [class.text-amber-400]="d.orderBalancePoints > 0"
                  [class.text-green-400]="d.orderBalancePoints <= 0">
                  {{ d.orderBalancePoints }} b.
                </p>
              </div>
            </div>
          </div>
        }

        @if (d.userPointBalance !== undefined && d.userPointBalance !== null && canPayWithPoints()) {
          <div class="bg-purple-500/10 border border-purple-500/20 rounded-xl p-4 space-y-3">
            <h3 class="text-sm font-semibold text-white flex items-center gap-2">
              <span class="material-symbols-outlined text-base text-purple-400">account_balance_wallet</span>
              {{ 'pos.tabs.detail.user_points_section' | translate }}
            </h3>
            <div class="flex items-center justify-between">
              <div>
                <p class="text-xs text-white/50 mb-1">{{ 'pos.tabs.user_available_points' | translate }}</p>
                <p class="text-xl font-bold text-purple-400">{{ d.userPointBalance }} b.</p>
              </div>
              @if (hasItemsWithPoints() && totalOrderPoints() > 0) {
                <div class="text-right">
                  <p class="text-xs text-white/50 mb-1">{{ 'pos.tabs.order_in_points' | translate }}</p>
                  <p class="text-xl font-bold text-amber-400">{{ totalOrderPoints() }} b.</p>
                </div>
              }
            </div>
          </div>
        }

        @if (ordersTimeline().length > 0) {
          <div class="bg-white/5 rounded-xl overflow-hidden">
            <button
              type="button"
              data-testid="history-toggle-button"
              (click)="toggleHistorySection()"
              class="w-full px-4 py-3 bg-white/5 border-b border-white/10 flex items-center justify-between hover:bg-white/10 transition-colors">
              <h3 class="text-sm font-semibold text-white flex items-center gap-2">
                <span class="material-symbols-outlined text-base">history</span>
                {{ 'pos.tabs.detail.history' | translate }}
              </h3>
              <span
                class="material-symbols-outlined text-white/50 transition-transform"
                [class.rotate-180]="historySectionExpanded()">
                expand_more
              </span>
            </button>
            @if (historySectionExpanded()) {
              <div class="max-h-[200px] overflow-y-auto">
                @for (entry of ordersTimeline(); track entry.orderId ?? entry.createdAt) {
                  @if (entry.type === OrderTimelineEntryType.Order && entry.items) {
                    <div class="px-4 py-3 border-b border-white/5 last:border-b-0">
                      <div class="flex items-center justify-between gap-3 mb-2">
                        <div class="flex items-center gap-2">
                          <span class="material-symbols-outlined text-base text-blue-400">inventory_2</span>
                          <span class="text-xs text-white/50">{{ entry.createdAt | date: 'dd.MM. HH:mm' }}</span>
                          <span class="text-xs text-white/70">{{ 'pos.tabs.detail.order' | translate }}</span>
                        </div>
                        @if (d.status === PosTabStatus.Open) {
                          <button
                            type="button"
                            data-testid="duplicate-order-button"
                            (click)="duplicateOrder(entry.items!, entry.totalCents ?? 0)"
                            class="px-2 py-1 rounded-lg bg-neon/10 text-neon text-xs font-medium hover:bg-neon/20 transition-colors flex items-center gap-1">
                            <span class="material-symbols-outlined text-sm">content_copy</span>
                            {{ 'pos.tabs.detail.duplicate' | translate }}
                          </button>
                        }
                      </div>
                      <p class="text-sm text-white/80 pl-6">
                        @for (item of entry.items; track item.id; let isLast = $last) {
                          {{ item.name }} x{{ item.qty }}
                          @if (!isLast) {
                            ,
                          }
                        }
                        <span class="text-white font-semibold ml-1"
                          >= {{ (entry.totalCents ?? 0) / 100 | number: '1.2-2' }} €</span
                        >
                      </p>
                    </div>
                  } @else if (entry.type === OrderTimelineEntryType.Payment) {
                    <div class="px-4 py-3 border-b border-white/5 last:border-b-0">
                      <div class="flex items-center justify-between gap-3">
                        <div class="flex items-center gap-2">
                          @if (entry.paymentAmountCents) {
                            <span class="material-symbols-outlined text-base text-green-400">payments</span>
                          } @else {
                            <span class="material-symbols-outlined text-base text-amber-400">star</span>
                          }
                          <span class="text-xs text-white/50">{{ entry.createdAt | date: 'dd.MM. HH:mm' }}</span>
                          <span class="text-xs text-white/70">{{ 'pos.tabs.detail.payment_entry' | translate }}</span>
                          @if (entry.paymentMethod) {
                            <span class="text-xs text-white/50">
                              ({{
                                entry.paymentMethod === PaymentMethod.Cash
                                  ? ('pos.checkout.cash' | translate)
                                  : ('pos.checkout.card' | translate)
                              }})
                            </span>
                          }
                        </div>
                        @if (entry.paymentAmountCents) {
                          <span class="text-sm font-bold text-green-400"
                            >+{{ entry.paymentAmountCents / 100 | number: '1.2-2' }} €</span
                          >
                        } @else if (entry.paymentPoints) {
                          <span class="text-sm font-bold text-amber-400">+{{ entry.paymentPoints }} b.</span>
                        }
                      </div>
                    </div>
                  } @else if (entry.type === OrderTimelineEntryType.Writeoff) {
                    <div class="px-4 py-3 border-b border-white/5 last:border-b-0">
                      <div class="flex items-center justify-between gap-3 mb-1">
                        <div class="flex items-center gap-2">
                          <span class="material-symbols-outlined text-base text-red-400">money_off</span>
                          <span class="text-xs text-white/50">{{ entry.createdAt | date: 'dd.MM. HH:mm' }}</span>
                          <span class="text-xs text-white/70">
                            {{
                              entry.writeoffIsFullWriteOff
                                ? ('pos.tabs.writeoff.full' | translate)
                                : ('pos.tabs.writeoff.partial' | translate)
                            }}
                          </span>
                        </div>
                        <span class="text-sm font-bold text-red-400"
                          >-{{ (entry.writeoffAmountCents ?? 0) / 100 | number: '1.2-2' }} €</span
                        >
                      </div>
                      @if (entry.writeoffReason) {
                        <p class="text-xs text-white/50 pl-6 italic">"{{ entry.writeoffReason }}"</p>
                      }
                    </div>
                  }
                }
              </div>
            }
          </div>
        }

        @if (groupedItems().length > 0) {
          <div class="bg-white/5 rounded-xl overflow-hidden">
            <button
              type="button"
              (click)="toggleItemsSection()"
              class="w-full px-4 py-3 bg-white/5 border-b border-white/10 flex items-center justify-between hover:bg-white/10 transition-colors">
              <h3 class="text-sm font-semibold text-white flex items-center gap-2">
                <span class="material-symbols-outlined text-base">receipt_long</span>
                {{ 'pos.tabs.detail.items' | translate }} ({{ groupedItems().length }})
              </h3>
              <span
                class="material-symbols-outlined text-white/50 transition-transform"
                [class.rotate-180]="itemsSectionExpanded()">
                expand_more
              </span>
            </button>
            @if (itemsSectionExpanded()) {
              <div class="max-h-[150px] overflow-y-auto">
                @for (item of groupedItems(); track item.productId) {
                  <div
                    class="px-4 py-3 border-b border-white/5 last:border-b-0 flex items-center justify-between gap-3">
                    <div class="flex-1 min-w-0">
                      <div class="flex items-center gap-2">
                        <span class="text-sm font-medium text-white truncate">{{ item.name }}</span>
                        @if (item.pointsQty > 0) {
                          <span
                            class="px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-400 text-[10px] font-medium shrink-0">
                            {{ 'pos.checkout.points' | translate }}
                          </span>
                        }
                        @if (item.writtenOffQty > 0) {
                          <span
                            class="px-1.5 py-0.5 rounded bg-red-500/20 text-red-400 text-[10px] font-medium shrink-0">
                            {{ 'pos.tabs.writeoff.written_off_badge' | translate: { qty: item.writtenOffQty } }}
                          </span>
                        }
                      </div>
                      <p class="text-xs text-white/50 mt-0.5">
                        {{ item.totalQty }}x
                        @if (item.moneyQty > 0) {
                          {{ item.unitPriceCents / 100 | number: '1.2-2' }} €
                        }
                        @if (item.unitPoints > 0) {
                          · {{ item.unitPoints }} b.
                        } @else if (item.moneyQty === 0) {
                          {{ item.unitPoints }} b.
                        }
                      </p>
                    </div>
                    <div class="text-right shrink-0">
                      @if (item.moneyTotalCents > 0) {
                        <span class="text-sm font-semibold text-white block"
                          >{{ item.moneyTotalCents / 100 | number: '1.2-2' }} €</span
                        >
                      }
                      @if (item.pointsTotalPoints > 0) {
                        <span class="text-sm font-semibold text-amber-400 block">{{ item.pointsTotalPoints }} b.</span>
                      }
                    </div>
                  </div>
                }
              </div>
            }
          </div>
        }

        <div class="bg-white/5 rounded-xl p-4 space-y-2">
          <div class="flex justify-between text-sm">
            <span class="text-white/50">{{ 'pos.tabs.detail.status' | translate }}</span>
            <span
              class="px-2 py-0.5 rounded-full text-xs"
              [ngClass]="{
                'bg-green-500/20': d.status === PosTabStatus.Open,
                'text-green-400': d.status === PosTabStatus.Open,
                'bg-red-500/20': d.status === PosTabStatus.Closed,
                'text-red-400': d.status === PosTabStatus.Closed,
              }">
              {{
                d.status === PosTabStatus.Open
                  ? ('pos.tabs.status.open' | translate)
                  : ('pos.tabs.status.closed' | translate)
              }}
            </span>
          </div>
          <div class="flex justify-between text-sm">
            <span class="text-white/50">{{ 'pos.tabs.detail.type' | translate }}</span>
            <span class="text-white">{{
              tab.type === PosTabType.User ? ('pos.tabs.type.user' | translate) : ('pos.tabs.type.visitor' | translate)
            }}</span>
          </div>
        </div>
      </div>
    }
  </div>

  @if (!loading() && detail()) {
    <div class="p-4 lg:p-6 border-t border-white/10 space-y-3">
      @if (detail()!.status === PosTabStatus.Open) {
        <button
          type="button"
          data-testid="tab-add-payment-button"
          (click)="showAddPayment()"
          class="w-full py-3 px-6 rounded-full bg-neon text-night font-bold hover:bg-neon/80 transition-all flex items-center justify-center gap-2 uppercase">
          <span class="material-symbols-outlined">add_card</span>
          {{ 'pos.tabs.add_payment' | translate }}
        </button>
      }

      @if (detail()!.status === PosTabStatus.Open && detail()!.orderBalanceCents > 0 && allowWriteOff) {
        <button
          type="button"
          data-testid="tab-writeoff-button"
          (click)="openWriteoffDialog()"
          class="w-full py-3 px-6 rounded-full bg-red-500/20 text-red-400 font-medium hover:bg-red-500/30 transition-all flex items-center justify-center gap-2">
          <span class="material-symbols-outlined">money_off</span>
          {{ 'pos.tabs.writeoff.button' | translate }}
        </button>
      }

      <button
        type="button"
        data-testid="tab-close-button"
        (click)="close()"
        class="w-full py-3 px-6 rounded-full border border-white/20 text-white font-medium hover:bg-white/10 transition-colors flex items-center justify-center gap-2">
        <span class="material-symbols-outlined">arrow_back</span>
        {{ 'pos.tabs.back' | translate }}
      </button>
    </div>
  }
</div>
